<script lang="ts">
    import { onMount } from 'svelte';
    import Limiter from './_wrappers/Limiter.svelte';
    import Transition from './_wrappers/Transition.svelte';
    import clsx from 'clsx';
    import TextOnImage from './Image/TextOnImage.svelte';

    // Utils
    import createSliderController from './_utils/sliderLogic';
    import { getWidthByScreenSize } from './_utils/getWidthByScreenSize';
    import { getBorderRadius } from '$lib/global/helper/border-radius';

    // Types
    import type { TransitionEffect } from '$lib/types/Theme';
    import type { Testimonial } from '$lib/types/Testimonial';
    import type { Layout } from '$lib/types/Components/SharedTypes';

    export let bgColor = 'transparent';
    export let testimonials: Testimonial[] = [];
    export let framed = false;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let layout: Layout;

    let offset = 0;

    const validTestimonials = testimonials.filter(
        (testimonial) =>
            typeof testimonial?.headline === 'string' &&
            typeof testimonial?.subHeadline === 'string' &&
            testimonial?.image &&
            testimonial?.mode,
    );

    const { start, next, back, ...sliderController } = createSliderController(
        validTestimonials,
        8000,
    );

    sliderController.offset.subscribe((newOffset: number) => {
        offset = newOffset;
    });

    const wrapperClasses = clsx(
        getBorderRadius('sliderImage'),
        'w-full overflow-hidden relative isolate border border-gray-200',
    );

    onMount(() => {
        start();
    });
</script>

{#if validTestimonials.length > 0}
    <div class="h-full w-full" style="transform: translateZ(0); background-color: {bgColor}">
        <Limiter>
            {#if validTestimonials.length === 1}
                <div class={framed ? 'p-6 sm:px-12 sm:py-6 md:px-20 md:py-10' : 'p-0'}>
                    <div class={wrapperClasses}>
                        <TextOnImage
                            image={validTestimonials[0].image}
                            headline={validTestimonials[0].headline}
                            subHeadline={validTestimonials[0].subHeadline}
                            mode={validTestimonials[0].mode}
                            fixedMaxWidth={getWidthByScreenSize()}
                        />
                    </div>
                </div>
            {:else}
                <Transition delay={transitionDelay} effect={transitionEffect}>
                    <div class={framed ? 'p-6 sm:px-12 sm:py-6 md:px-20 md:py-10' : 'p-0'}>
                        <div class={wrapperClasses}>
                            <button
                                on:click={back}
                                class="absolute bottom-0 left-0 top-0 z-30 w-1/2 cursor-pointer"
                            />
                            <button
                                on:click={next}
                                class="absolute bottom-0 right-0 top-0 z-30 w-1/2 cursor-pointer"
                            />
                            <div
                                class="absolute bottom-3 left-0 right-0 z-20 flex w-full justify-center space-x-1 px-3"
                            >
                                {#each validTestimonials as testimonial}
                                    <div
                                        class:bg-white={validTestimonials.indexOf(testimonial) <
                                            offset && validTestimonials[offset].mode === 'dark'}
                                        class:bg-gray-400={validTestimonials.indexOf(testimonial) >=
                                            offset && validTestimonials[offset].mode === 'dark'}
                                        class:bg-gray-300={validTestimonials.indexOf(testimonial) <
                                            offset && validTestimonials[offset].mode === 'light'}
                                        class:bg-gray-200={validTestimonials.indexOf(testimonial) >=
                                            offset && validTestimonials[offset].mode === 'light'}
                                        class="h-1 w-full max-w-[70px] flex-grow overflow-hidden rounded-sm transition"
                                    >
                                        <div
                                            style="animation-fill-mode: forwards"
                                            class:bg-white={testimonial.mode === 'dark'}
                                            class:bg-gray-300={testimonial.mode === 'light'}
                                            class="h-full w-0"
                                            class:animate-fill-slow={validTestimonials.indexOf(
                                                testimonial,
                                            ) === offset}
                                        />
                                    </div>
                                {/each}
                            </div>
                            <div
                                class="flex transition duration-700"
                                style="width: calc({validTestimonials.length} * 100%); transform: translateX(calc({offset} * (-100% / {validTestimonials.length})))"
                            >
                                {#each validTestimonials as testimonial}
                                    <TextOnImage
                                        image={testimonial.image}
                                        headline={testimonial.headline}
                                        subHeadline={testimonial.subHeadline}
                                        mode={testimonial.mode}
                                        fixedMaxWidth={getWidthByScreenSize()}
                                        isInMultiColumn={layout.isInMultiColumn}
                                    />
                                {/each}
                            </div>
                        </div>
                    </div>
                </Transition>
            {/if}
        </Limiter>
    </div>
{:else}
    <div class="hidden">No valid testimonial slides</div>
{/if}
